import axios from "axios";
import { API_BASE } from "../config";

// const API = axios.create({ baseURL: 'http://localhost:5000'})
const API = axios.create({ baseURL: API_BASE });

API.interceptors.request.use((req) => {
  if (localStorage.getItem("profile")) {
    req.headers.authorization = `Bearer ${JSON.parse(localStorage.getItem("profile")).token}`;
  }

  return req;
});

// server/middleware/auth.js returns 401 only when the bearer token itself is
// unusable (missing, malformed, wrong algorithm, expired, or doesn't decode
// to a real user) - login failures use 400/404, never 401, so this can't
// misfire on a wrong-password attempt. Without this, whatever tab/process
// is holding a permanently-invalid token (corrupted, tampered with, or from
// an incompatible source) never actually gets logged out - it just keeps
// re-sending the same doomed token forever. That's exactly what
// useSessionKeepAlive.js's silent "leave the old token in place, the normal
// logout will catch it eventually" retry loop was relying on a logout path
// that didn't actually exist anywhere: it decodes (never verifies) the
// token's own `exp` claim client-side, so a malformed-but-not-technically-
// "expired" token never naturally expires from the client's point of view,
// and the 15s-minimum retry timer just hammers the server indefinitely.
// This is that missing logout path - the one place every API call already
// passes through.
API.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401) {
      try {
        localStorage.removeItem("profile");
      } catch (storageError) {
        // Private-browsing/storage-blocked - nothing more to clean up.
      }
      // Hard redirect, not history.push - this module runs outside the
      // React tree (no access to the app's Redux dispatch or router
      // history), and a 401 is rare enough in normal use that the one-time
      // full reload it costs is a fair trade for actually ending a session
      // that's stuck sending a token the server will never accept.
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// export const fetchInvoices =() => API.get('/invoices')
export const fetchInvoice = (id) => API.get(`/invoices/${id}`);
export const fetchInvoicesByClient = ({
  page,
  rowsPerPage,
  id,
  startDate,
  endDate,
}) =>
  API.post(`/invoices/client`, { page, rowsPerPage, id, startDate, endDate });
export const addInvoice = (invoice) => API.post("/invoices", invoice);
export const updateInvoice = (id, updatedInvoice) =>
  API.post(`/invoices/${id}`, updatedInvoice);
export const deleteInvoice = (id) => API.post(`/invoices/delete/${id}`);
export const fetchInvoicesByUser = (searchQuery) =>
  API.get(`/invoices/user?searchQuery=${searchQuery.search}`);
export const fetchInvoices = () => API.get("/invoices");
export const fetchInvoicesCount = () => API.get("/invoices/count");
// Paginated/filtered listing for the Invoices list page - fetchInvoices
// above is unrelated and stays as the full-collection fetch the Daily
// Sales Report dialog on that same page relies on.
export const searchInvoices = (params) =>
  API.get("/invoices/search", { params: params || {} });

export const fetchDeletedInvoices = () => API.get("/invoices/deleted"); // Add this
export const softdeleteInvoice = (id, body) => API.post(`/invoices/softdelete/${id}`, body);

export const createCashTransaction = (transaction) => API.post("/cash-transactions", transaction);
export const fetchCashTransactions = (startDate, endDate) =>
  API.get(`/cash-transactions?startDate=${startDate}&endDate=${endDate}`);
export const closeRegister = (data) => API.post("/cash-transactions/close", data);
export const fetchCashSummary = (date) => API.get(`/cash-transactions/summary?date=${date}`);
export const generateDailyReport = (date) => API.get(`/cash-transactions/report?date=${date}`);

export const updateStaffCommissionDetail = (id, updatedStaffCommissionDetail) =>
  API.post(`/staffCommissionDetails/update/${id}`, updatedStaffCommissionDetail);
export const fetchStaffCommissionDetail = (id) =>
  API.get(`/staffCommissionDetails/${id}`);
export const addStaffCommissionDetail = (staffCommissionDetail) =>
  API.post("/staffCommissionDetails", staffCommissionDetail);


export const updateStaffCommissionDetails = async (
  updatedStaffCommissionDetails
) => {
  try {
    console.log(
      "Update StaffCommissionDetails from API index: ",
      updatedStaffCommissionDetails
    );
    const response = await API.post(
      `/staffCommissionDetails/update`,
      updatedStaffCommissionDetails
    );

    if (response.status === 200) {
      console.log("staffCommissionDetails updated successfully");
    } else {
      console.error("Failed to update staffCommissionDetails");
    }
  } catch (error) {
    console.error(
      "An error occurred while updating staffCommissionDetails:",
      error
    );
  }
};
export const deleteStaffCommissionDetail = (id) =>
  API.post(`/staffCommissionDetails/delete/${id}`);
export const fetchStaffCommissionDetailsByUser = (searchQuery) =>
  API.get(`/staffCommissionDetails/user?searchQuery=${searchQuery.search}`);
export const fetchStaffCommissionDetails = () =>
  API.get("/staffCommissionDetails");

export const fetchPurchase = (id) => API.get(`/purchases/${id}`);
export const addPurchase = (purchase) => API.post("/purchases", purchase);
export const updatePurchase = (id, updatedPurchase) =>
  API.post(`/purchases/${id}`, updatedPurchase);
export const deletePurchase = (id) => API.post(`/purchases/delete/${id}`);
export const softdeletePurchase = (id, body) => API.post(`/purchases/softdelete/${id}`, body);
export const fetchDeletedPurchases = () => API.get("/purchases/deleted");
export const fetchPurchasesByUser = (searchQuery) =>
  API.get(`/purchases/user?searchQuery=${searchQuery.search}`);
export const fetchPurchases = () => API.get("/purchases");
export const fetchPurchasesCount = (searchQuery) =>
  API.get(`/purchases/count?searchQuery=${searchQuery}`);
// Paginated/filtered listing for the Purchases list page - fetchPurchases
// above is unrelated and stays as the full-collection fetch other pages
// (Dashboard, SupplierDues, SuppliersList) rely on.
export const searchPurchases = (params) =>
  API.get("/purchases/search", { params: params || {} });

export const fetchMeeting = (id) => API.get(`/meetings/${id}`);
export const addMeeting = (meeting) => API.post("/meetings", meeting);
export const updateMeeting = (id, updatedMeeting) =>
  API.post(`/meetings/${id}`, updatedMeeting);
export const deleteMeeting = (id) => API.post(`/meetings/delete/${id}`);
export const fetchPaymentsByClient = ({
  page,
  rowsPerPage,
  id,
  startDate,
  endDate,
}) =>
  API.post(`/meetings/client`, { page, rowsPerPage, id, startDate, endDate });
export const fetchMeetingsByUser = (searchQuery) =>
  API.get(`/meetings?searchQuery=${searchQuery.search}`);
export const fetchMeetings = () => API.get("/meetings");

export const fetchClient = (id) => API.get(`/clients/${id}`);
export const fetchClients = (page) => API.get(`/clients?page=${page}`);
export const addClient = (client) => API.post("/clients", client);
export const updateClient = (id, updatedClient) =>
  API.post(`/clients/${id}`, updatedClient);
export const deleteClient = (id) => API.post(`/clients/delete/${id}`);
export const fetchClientsByUser = (searchQuery) =>
  API.get(`/clients/user?searchQuery=${searchQuery.search}`);
export const fetchAllClients = () => API.get("/clients");

export const fetchSupplier = (id) => API.get(`/suppliers/${id}`);
export const fetchSuppliers = (page) => API.get(`/suppliers?page=${page}`);
export const addSupplier = (supplier) => API.post("/suppliers", supplier);
export const updateSupplier = (id, updatedSupplier) =>
  API.post(`/suppliers/${id}`, updatedSupplier);
export const deleteSupplier = (id) => API.post(`/suppliers/delete/${id}`);
export const fetchSuppliersByUser = (searchQuery) =>
  API.get(`/suppliers/user?searchQuery=${searchQuery.search}`);
export const fetchAllSuppliers = () => API.get("/suppliers");

// export const adminSignIn =(formData)=> API.post('/users/adminSignin', formData)
export const adminSignUp = (formData) =>
  API.post("/users/adminSignup", formData);
export const signIn = (formData) => API.post("/users/signin", formData);
export const signUp = (formData) => API.post("/users/signup", formData);
export const logoutSession = () => API.post("/users/logout");
// Exchanges a still-valid token for a fresh one (sliding session).
export const refreshSession = () => API.post("/users/refresh");
export const forgot = (formData) => API.post("/users/forgot", formData);
export const reset = (formData) => API.post("/users/reset", formData);
export const fetchUsers = () => API.get("/users");
export const deleteUser = (id) => API.post(`/users/delete/${id}`);
export const updateUser = (id, updatedUser) =>
  API.post(`/users/${id}`, updatedUser);
export const adminChangePassword = (userId, newPassword) =>
  API.post(`/users/change-password/${userId}`, { newPassword });
// SuperAdmin-only - see requireSuperAdmin on this route server-side. 404s
// with a message when the target has no stored reversible password yet
// (existing users, until their password is next set/changed/reset).
export const revealUserPassword = (id) => API.get(`/users/${id}/reveal-password`);
export const changeMyPassword = (newPassword) =>
  API.post(`/users/change-my-password`, { newPassword });

export const fetchProfilesBySearch = (searchQuery) =>
  API.get(
    `/profiles/search?searchQuery=${
      searchQuery.search || searchQuery.year || "none"
    }`
  );
export const fetchCompany = () => API.get('/company');
export const updateCompany = (formData) => API.post('/company', formData);
export const fetchPublicCompany = () => API.get('/company/public');
export const fetchProfile = (id) => API.get(`/profiles/${id}`);
// The store a given user belongs to (business name/address/phone/website) -
// backs SuperAdmin's per-user "View Profile" dialog. 404s if that user has
// no linked Profile document.
export const fetchProfileByUserId = (userId) => API.get(`/profiles/by-user/${userId}`);
export const fetchProfiles = () => API.get("/profiles");
// Unauthenticated, returns only { businessName } - backs the public
// Privacy Policy page so a first-time visitor with no session still sees
// the real business name, not just a generic fallback.
export const fetchPublicBusinessName = () => API.get("/company/public");
export const fetchProfilesByUser = (searchQuery) =>
  API.get(`/profiles?searchQuery=${searchQuery.search}`);
// console.log("fetchProfilesByUser called in client")
// console.log(searchQuery.search)

export const createProfile = (newProfile) => API.post("/profiles", newProfile);
export const updateProfile = (id, updatedProfile) =>
  API.post(`/profiles/${id}`, updatedProfile);
export const deleteProfile = (id) => API.post(`/profiles/delete/${id}`);

export const createFuturePriceList = (futurePriceList) =>
  API.post("/items/future-prices", futurePriceList);
export const fetchFuturePriceList = () => API.get("/items/future-prices");
export const updateFuturePrice = (id, priceData) =>
  API.post(`/items/future-prices/update/${id}`, priceData);
export const deleteFuturePrice = (id) => API.post(`/items/future-prices/delete/${id}`);

export const fetchItem = (id) => API.get(`/items/${id}`);
export const fetchItems = (params) => API.get("/items", { params });
// Server-side search (name + barcode) - filters in the DB instead of
// shipping the whole catalog to filter client-side on every keystroke.
export const searchItems = (q) => API.get("/items/search", { params: { q } });
export const addItem = (item) => API.post("/items", item);
export const addItems = (item) => API.post("/items/file", item);
export const updateItem = (id, updatedItem) =>
  API.post(`/items/update/${id}`, updatedItem);
export const toggleItemStatus = (id) => API.post(`/items/toggle/${id}`);

export const deleteItem = (id) => API.post(`/items/delete/${id}`);
export const deleteItemsByCategoryId = (id) => API.post(`/items/deleteByCategoryId/${id}`);
export const fetchItemsByUser = (searchQuery) =>
  API.get(`/items/user?searchQuery=${searchQuery.searcategoriesch}`);
export const updateItems = (items) => API.post("/items/update", items);
// Dedicated fast path for bulk-upload updates - single bulkWrite server-side
// instead of N sequential findByIdAndUpdate+populate calls. Used only by the
// bulk-upload flow; every other caller of updateItems is untouched.
export const bulkUpdateItems = (items) => API.post("/items/bulk-update", items);

export const fetchUnits=()=>API.get(`/units`)
export const addUnit=(unit)=>API.post(`/units`, unit)
export const addUnits=(unit)=>API.post(`/units/createAll`, unit)
export const getUnitById=(id)=>API.get(`/units/${id}`)
export const mapUnit = (mappingData) => API.post("/units/map", mappingData);

export const deleteBasicUnit = (id) => API.post(`/units/delete-basic/${id}`);
export const deleteMappedUnit = (id) => API.post(`/units/delete-mapped/${id}`);
export const updateBasicUnit = (id, unit) => API.post(`/units/update-basic/${id}`, unit);
export const updateMappedUnit = (id, unit) => API.post(`/units/update-mapped/${id}`, unit);


// Unit API Calls
export const fetchUnitMappings = () => API.get("/units/mappings");
export const createUnit = (newUnit) => API.post("/units", newUnit);
export const convertUnits = (conversionData) => API.post("/units/convert", conversionData);
export const updateUnit = (id, updatedUnit) => API.post(`/units/update/${id}`, updatedUnit);
export const deleteUnit = (id) => API.post(`/units/delete/${id}`);
export const deleteUnitMapping = (id) => API.post(`/units/delete-mapping/${id}`);
export const updateUnitMapping = (id, mappingData) => axios.post(`/unit-mappings/update/${id}`, mappingData);



export const fetchCategory = (id) => API.get(`/categories/${id}`);
export const addCategory = (stock) => API.post("/categories", stock);



export const updateCategory = (id, updatedCategory) =>
  API.post(`/categories/${id}`, updatedCategory);
export const toggleCategoryStatus = (id) => API.post(`/categories/toggle/${id}`);
export const deleteCategory = (id) => API.post(`/categories/delete/${id}`);
export const fetchCategoriesByUser = (searchQuery) =>
  API.get(`/categories/user?searchQuery=${searchQuery.search}`);
export const fetchCategories = () => API.get("/categories");
// Server-side search (category name) - see searchItems above for why.
export const searchCategories = (q) => API.get("/categories/search", { params: { q } });

export const fetchStock = (id) => API.get(`/stocks/${id}`);
export const addStock = (stock) => API.post("/stocks", stock);
export const updateStock = (id, updatedStock) =>
  API.post(`/stocks/${id}`, updatedStock);
export const deleteStock = (id) => API.post(`/stocks/delete/${id}`);
export const deleteStockById = (id, type) => API.post(`/stocks/${type}/${id}`);
export const fetchStocksByUser = (searchQuery) =>
  API.get(`/stocks/user?searchQuery=${searchQuery.search}`);
export const fetchStocks = (endDate) =>
  API.get(endDate ? `/stocks?endDate=${endDate}` : "/stocks");

export const getCurrentStockQuantity=(itemId)=>{
  // TEMPORARY diagnostic - remove once the repeated-call caller is confirmed.
  console.trace(`[DIAG] GET /stocks/quantity/${itemId}`);
  return API.get(`/stocks/quantity/${itemId}`);
};
export const getBulkStockQuantities = (itemIds) => API.post("/stocks/quantities", { itemIds });
export const fetchStockValuation = () => API.get("/stocks/valuation");




export const fetchStockAdjustment = (id) => API.get(`/stockAdjustments/${id}`);
export const addStockAdjustment = (stockAdjustment) =>
  API.post("/stockAdjustments", stockAdjustment);
export const updateStockAdjustment = (id, updatedStockAdjustment) =>
  API.post(`/stockAdjustments/${id}`, updatedStockAdjustment);
export const deleteStockAdjustment = (id) =>
  API.post(`/stockAdjustments/delete/${id}`);
export const softdeleteStockAdjustment = (id, body) =>
  API.post(`/stockAdjustments/softdelete/${id}`, body);
export const fetchDeletedStockAdjustments = () => API.get("/stockAdjustments/deleted");
export const fetchStockAdjustmentsByUser = (searchQuery) =>
  API.get(`/stockAdjustments?searchQuery=${searchQuery.search}`);
export const fetchStockAdjustments = () => API.get("/stockAdjustments");
export const fetchStockAdjustmentsCount = (searchQuery) =>
  API.get(`/stockAdjustments/count?searchQuery=${searchQuery}`);
// Paginated/filtered listing for the Stock Adjustments list page -
// fetchStockAdjustments above is unrelated and stays as the full-collection
// fetch other pages may rely on.
export const searchStockAdjustments = (params) =>
  API.get("/stockAdjustments/search", { params: params || {} });

export const fetchStaff = (id) => API.get(`/staff/${id}`);
export const fetchStaffByPage = (page) => API.get(`/staff?page=${page}`);
export const addStaff = (client) => API.post("/staff", client);
export const updateStaff = (id, updatedStaff) =>
  API.post(`/staff/${id}`, updatedStaff);
export const deleteStaff = (id) => API.post(`/staff/delete/${id}`);
export const fetchStaffByUser = (searchQuery) =>
  API.get(`/staff/user?searchQuery=${searchQuery.search}`);
export const fetchAllStaff = () => API.get("/staff");

// New User Type API Calls
// User Type API Calls
export const fetchUserTypes = () => API.get("/users/userTypes");
export const createUserType = (userTypeData) => {
  console.log("API createUserType called with URL:", "/users/userTypes", "data:", userTypeData);
  return API.post("/users/userTypes", userTypeData);
};
export const updateUserType = (name, userTypeData) => {
  console.log("Updating user type with URL:", `/users/userTypes/update/${name}`); 
  return API.post(`/users/userTypes/update/${name}`, userTypeData);
};
export const deleteUserType = (name) => {
  console.log("Deleting user type with URL:", `/users/userTypes/delete/${name}`); 
  return API.post(`/users/userTypes/delete/${name}`);
};
export const getUserPermissions = () => API.get('/users/permissions');

// Subscription Management APIs
export const getSubscription = () => API.get("/subscription");
export const updateSubscription = (subscriptionData) =>
  API.post("/subscription/update", subscriptionData);

// Global settings (superAdmin-only) - customInvoiceFormatEntitled,
// useCustomInvoiceFormat, enableA4InvoiceView (mirrored onto every Profile
// document server-side), plus the expiry-badge/reminder-days config.
export const getGlobalSettings = () => API.get("/global-settings");
export const updateGlobalSettings = (settings) =>
  API.post("/global-settings/update", settings);
export const purgeProgramLogs = () => API.post("/global-settings/purge-program-logs");


export const uploadLogo = (logoBase64) => {
  return API.post("/company-logo/upload", { logoBase64 });
};

export const getLogo = () => {
  return API.get("/company-logo/logo");
};

// Employee Master APIs (GET and POST only)
export const fetchEmployees = () => API.get("/employees");
export const fetchEmployee = (id) => API.get(`/employees/${id}`);
export const addEmployee = (employee) => API.post("/employees", employee);
export const updateEmployee = (id, employee) =>
  API.post(`/employees/${id}`, employee);

// Shift APIs (session lifecycle per employee)
export const getMyActiveShift = () => API.get("/shifts/my-active");
export const getActiveShifts = () => API.get("/shifts/active");
export const getShiftLogs = (params) =>
  API.get("/shifts/logs", { params: params || {} });
export const getLastShiftByEmployee = () => API.get("/shifts/last-by-employee");
export const clearShiftLogs = (startDate, endDate) =>
  API.post("/shifts/logs/clear", { startDate, endDate });
export const startShift = (payload) =>
  API.post("/shifts/start", typeof payload === "object" ? payload : { userName: payload });
export const endShift = () => API.post("/shifts/end");

// Activity Logs (admin-only activity history across all modules)
export const getProgramLogs = (params) =>
  API.get("/program-logs", { params: params || {} });
export const migrateProgramLogs = () =>
  API.post("/program-logs/migrate");
export const recordProgramLogEvent = (payload) =>
  API.post("/program-logs/events", payload);

// Order Sessions (Hold Bill) APIs - GET and POST only
export const createOrderSession = (data) =>
  API.post("/order-sessions", data);
export const getOrderSessions = (params) =>
  API.get("/order-sessions", { params: params || {} });
export const getOrderSession = (id) =>
  API.get(`/order-sessions/${id}`);
export const addItemToOrderSession = (id, item) =>
  API.post(`/order-sessions/${id}/add-item`, item);
export const removeItemFromOrderSession = (id, itemIdOrPayload) =>
  API.post(
    `/order-sessions/${id}/remove-item`,
    typeof itemIdOrPayload === "object" && itemIdOrPayload !== null
      ? itemIdOrPayload
      : { itemId: itemIdOrPayload }
  );
export const updateItemInOrderSession = (id, itemIdOrPayload, qtyOrUndefined) =>
  API.post(
    `/order-sessions/${id}/update-item`,
    typeof itemIdOrPayload === "object" && itemIdOrPayload !== null
      ? itemIdOrPayload
      : { itemId: itemIdOrPayload, qty: qtyOrUndefined }
  );
export const checkoutOrderSession = (id, paymentData) =>
  API.post(`/order-sessions/${id}/checkout`, paymentData);
export const printKot = (id, itemIds) =>
  API.post(`/order-sessions/${id}/print-kot`, { itemIds });

// Goods Return Management APIs
export const createGoodsReturn = (returnData) =>
  API.post("/returns", returnData);
export const fetchGoodsReturns = (page, limit, filters) =>
  API.get(`/returns?page=${page}&limit=${limit}${filters ? `&${new URLSearchParams(filters).toString()}` : ""}`);
export const fetchGoodsReturnHistory = (id) =>
  API.get(`/returns/history/${id}`);
export const approveGoodsReturn = (id) =>
  API.post(`/returns/${id}/approve`);
export const rejectGoodsReturn = (id, rejectionReason) =>
  API.post(`/returns/${id}/reject`, { rejectionReason });
export const processGoodsReturnRefund = (id, refundReference) =>
  API.post(`/returns/${id}/refund`, { refundReference });
// Both endpoints stream back a PDF binary (not JSON) — responseType 'blob'
// is required or axios will try to parse the bytes as text/JSON and corrupt
// the file.
export const generateGoodsReturnPdf = (id) =>
  API.post(`/returns/${id}/generate-pdf`, null, { responseType: "blob" });
export const generateGoodsReturnInvoicePdf = (id) =>
  API.post(`/returns/${id}/generate-invoice-pdf`, null, { responseType: "blob" });
export const updateGoodsReturn = (id, updateData) =>
  API.post(`/returns/${id}`, updateData);
export const softDeleteGoodsReturn = (id) =>
  API.post(`/returns/${id}/softdelete`);
export const fetchGoodsReturnsByInvoice = (invoiceId, page, limit) =>
  API.get(`/returns/invoice/${invoiceId}?page=${page}&limit=${limit}`);
